import React, { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import {
    getTenantBranding,
    saveTenantBranding,
    TenantWhiteLabelConfig,
} from '@/product/lib/tenantBranding';
import {
    getTenantVocabulary,
    saveTenantVocabulary,
    TenantVocabulary,
    VocabularyPreset,
    VOCABULARY_PRESETS,
} from '@/product/lib/tenantVocabulary';

interface WhiteLabelContextValue {
    branding: TenantWhiteLabelConfig;
    vocabulary: TenantVocabulary;
    updateBranding: (partial: Partial<TenantWhiteLabelConfig>) => void;
    updateVocabulary: (partial: Partial<TenantVocabulary>) => void;
    setVocabularyPreset: (preset: VocabularyPreset) => void;
}

const WhiteLabelContext = createContext<WhiteLabelContextValue | null>(null);

export function WhiteLabelProvider({ children }: { children: ReactNode }) {
    const [branding, setBrandingState] = useState<TenantWhiteLabelConfig>(getTenantBranding);
    const [vocabulary, setVocabularyState] = useState<TenantVocabulary>(getTenantVocabulary);

    useEffect(() => {
        const next = getTenantBranding();
        setBrandingState(next);
        setVocabularyState(getTenantVocabulary());
        if (next.brandColor) {
            document.documentElement.style.setProperty('--brand-primary', next.brandColor);
        }
    }, []);

    const updateBranding = (partial: Partial<TenantWhiteLabelConfig>) => {
        const next = saveTenantBranding(partial);
        setBrandingState(next);
    };

    const updateVocabulary = (partial: Partial<TenantVocabulary>) => {
        const next = saveTenantVocabulary(partial);
        setVocabularyState(next);
    };

    const setVocabularyPreset = (preset: VocabularyPreset) => {
        const base = VOCABULARY_PRESETS[preset] || VOCABULARY_PRESETS.restoration;
        const next = saveTenantVocabulary({ ...base, preset });
        setVocabularyState(next);
    };

    return (
        <WhiteLabelContext.Provider
            value={{
                branding,
                vocabulary,
                updateBranding,
                updateVocabulary,
                setVocabularyPreset,
            }}
        >
            {children}
        </WhiteLabelContext.Provider>
    );
}

export function useWhiteLabel(): WhiteLabelContextValue {
    const ctx = useContext(WhiteLabelContext);
    if (!ctx) {
        throw new Error('useWhiteLabel must be used within WhiteLabelProvider');
    }
    return ctx;
}

export function useWhiteLabelOptional(): WhiteLabelContextValue | null {
    return useContext(WhiteLabelContext);
}
